import { FiRotateCw } from 'react-icons/fi';

export const inversionData = {
  courseId: 'inversion',
  courseType: 'grammar',
  title: 'Inversion for Emphasis',
  icon: FiRotateCw,
  overview: {
    impact: 'Achieve Band 7+ through sophisticated sentence structures',
    focus: 'Subject-verb inversion after negative adverbials',
    uses: 'Writing Task 2, formal speaking',
    duration: '25 minutes'
  },
  sections: [
    {
      title: 'Negative Adverbial Inversion',
      content: `<p>When a negative expression starts a sentence, invert the subject and auxiliary:</p>`,
      examples: [
        {
          label: 'Normal',
          text: 'I have never seen such beauty.'
        },
        {
          label: 'Inverted',
          text: 'Never have I seen such beauty.'
        }
      ],
      points: [
        'Never have I..., Rarely does she..., Seldom do they...',
        'Not only did he pass, but he also got the highest score.',
        'Under no circumstances should students cheat.',
        'Only after the exam did I realize my mistake.'
      ]
    },
    {
      title: 'Common Inversion Patterns',
      content: `<p>These patterns score highly in IELTS Writing:</p>`,
      table: {
        headers: [
          'Pattern',
          'Example'
        ],
        rows: [
          [
            'Not only...but also',
            'Not only is exercise good for health, but it also improves mood.'
          ],
          [
            'Hardly...when',
            'Hardly had I arrived when it started raining.'
          ],
          [
            'Only by/after/when',
            'Only by working together can we solve this problem.'
          ],
          [
            'No sooner...than',
            'No sooner had she left than the phone rang.'
          ]
        ]
      }
    }
  ],
  exercises: [
    {
      title: 'Rewrite with Inversion',
      type: 'select',
      instructions: 'Choose the correct inverted form.',
      questions: [
        {
          text: 'I have never eaten such delicious food. → Never ___',
          options: [
            'have I eaten',
            'I have eaten',
            'did I eat'
          ],
          answer: 'have I eaten',
          explanation: 'Never + auxiliary + subject: Never have I eaten.'
        },
        {
          text: 'She not only speaks English but also French. → Not only ___',
          options: [
            'does she speak English',
            'she speaks English',
            'is she speaking'
          ],
          answer: 'does she speak English',
          explanation: 'Not only + auxiliary + subject + verb.'
        }
      ]
    },
    {
      title: 'Identify Inverted Sentences',
      type: 'select',
      instructions: 'Which sentence uses inversion correctly?',
      questions: [
        {
          text: 'Which sentence uses inversion?',
          options: [
            'Never have I seen such a view.',
            'I have never seen such a view.',
            'I never have seen such a view.'
          ],
          answer: 'Never have I seen such a view.',
          explanation: 'Inversion places the auxiliary (have) before the subject (I) after a negative adverbial (Never).'
        },
        {
          text: 'Which sentence uses inversion?',
          options: [
            'Rarely does she go out at night.',
            'She rarely goes out at night.',
            'She does rarely go out at night.'
          ],
          answer: 'Rarely does she go out at night.',
          explanation: '\'Rarely\' at the start triggers inversion: auxiliary (does) + subject (she) + base verb.'
        },
        {
          text: 'Which sentence uses inversion?',
          options: [
            'Not only did he apologise, but he also sent flowers.',
            'He not only apologised but also sent flowers.',
            'He did not only apologise but also sent flowers.'
          ],
          answer: 'Not only did he apologise, but he also sent flowers.',
          explanation: '\'Not only\' at the start requires inversion: did + subject + verb.'
        },
        {
          text: 'Which sentence uses inversion?',
          options: [
            'Only after the meeting did I understand the issue.',
            'I understood the issue only after the meeting.',
            'I did understand only after the meeting.'
          ],
          answer: 'Only after the meeting did I understand the issue.',
          explanation: '\'Only after\' at the start triggers inversion in the main clause.'
        }
      ]
    },
    {
      title: 'Complete with Inversion',
      type: 'fill-in',
      instructions: 'Fill in the missing auxiliary verb to complete each inverted sentence.',
      questions: [
        {
          text: 'Never ___ I experienced such kindness.',
          answer: 'have',
          explanation: 'Present perfect inversion: Never + have + subject. \'Never have I experienced...\''
        },
        {
          text: 'Not only ___ she win the race, but she also broke the record.',
          answer: 'did',
          explanation: 'Past simple inversion: Not only + did + subject + base verb.'
        },
        {
          text: 'Rarely ___ we see such talent in young musicians.',
          answer: 'do',
          explanation: 'Present simple inversion: Rarely + do + subject + base verb.'
        },
        {
          text: 'Hardly ___ he finished speaking when the alarm went off.',
          answer: 'had',
          explanation: 'Past perfect inversion: Hardly + had + subject. \'Hardly had he finished...\''
        }
      ]
    },
    {
      title: 'Rewrite Using Inversion',
      type: 'writing',
      instructions: 'Rewrite each sentence using inversion for emphasis. Start with the word(s) given.',
      questions: [
        {
          text: 'I had never felt so nervous before. (Start with: Never before...)',
          sampleAnswer: 'Never before had I felt so nervous.',
          explanation: 'Move the negative adverb to the front and invert: Never before + had + I + past participle.'
        },
        {
          text: 'She not only passed the test but also got the highest mark. (Start with: Not only...)',
          sampleAnswer: 'Not only did she pass the test, but she also got the highest mark.',
          explanation: '\'Not only\' at the start requires did + subject + base verb in the first clause.'
        },
        {
          text: 'We rarely have the opportunity to travel abroad. (Start with: Rarely...)',
          sampleAnswer: 'Rarely do we have the opportunity to travel abroad.',
          explanation: '\'Rarely\' at the start requires do + subject + base verb.'
        }
      ]
    }
  ],
  tips: [
    'Inversion shows advanced grammar range (Band 7+)',
    'Use sparingly — 1-2 times per essay is enough',
    'Common in formal/academic writing',
    'Practice: Never, Rarely, Not only, Only by/after'
  ],
  dailyPractice: [
    'Practice inversion for emphasis concepts daily',
    'Review and apply in IELTS practice tests',
    'Write example sentences using key concepts',
    'Teach the concept to someone else to reinforce understanding'
  ],
  backPath: '/exams/english/#grammar',
  backLabel: 'Back to Grammar',
  aiContext: 'Inversion: Subject-auxiliary inversion after negative adverbials (never, rarely, not only). Shows sophisticated grammar for Band 7+ in IELTS Writing.'
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...inversionData,
      title: inversionData.title + ' (Foundations)',
      overview: { ...inversionData.overview, duration: '15-20 minutes' },
      sections: inversionData.sections.slice(0, Math.max(2, Math.ceil(inversionData.sections.length / 2))),
      exercises: inversionData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...inversionData,
      title: inversionData.title + ' (Advanced)',
      overview: { ...inversionData.overview, duration: '30-35 minutes' },
      sections: inversionData.sections,
      exercises: inversionData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(inversionData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS tasks.',
      ],
    };
  }
  return inversionData;
}
